import { useState, useEffect, useCallback } from "react";
import { Button } from "@heroui/react";
import { LuX, LuChevronLeft, LuChevronRight } from "react-icons/lu";

/**
 * ImageLightbox - Reusable lightbox component for viewing images
 *
 * @param {boolean} isOpen - Whether lightbox is open
 * @param {Function} onClose - Callback to close lightbox
 * @param {Array} images - Array of image URLs or objects with url property
 * @param {number} initialIndex - Starting image index (default: 0)
 * @param {string} urlKey - Key to access URL if images is array of objects (default: 'url_600')
 */
const ImageLightbox = ({
    isOpen,
    onClose,
    images = [],
    initialIndex = 0,
    urlKey = "url_600",
}) => {
    const [currentIndex, setCurrentIndex] = useState(initialIndex);

    // Reset index when opening with new initialIndex
    useEffect(() => {
        if (isOpen) {
            setCurrentIndex(initialIndex);
        }
    }, [isOpen, initialIndex]);

    // Handle keyboard navigation
    useEffect(() => {
        if (!isOpen) return;

        const handleKeyDown = (e) => {
            if (e.key === "Escape") {
                onClose();
            } else if (e.key === "ArrowLeft") {
                goToPrev();
            } else if (e.key === "ArrowRight") {
                goToNext();
            }
        };

        document.addEventListener("keydown", handleKeyDown);
        document.body.style.overflow = "hidden";

        return () => {
            document.removeEventListener("keydown", handleKeyDown);
            document.body.style.overflow = "";
        };
    }, [isOpen, currentIndex, images.length]);

    const goToPrev = useCallback(() => {
        if (images.length <= 1) return;
        setCurrentIndex((prev) => (prev === 0 ? images.length - 1 : prev - 1));
    }, [images.length]);

    const goToNext = useCallback(() => {
        if (images.length <= 1) return;
        setCurrentIndex((prev) => (prev === images.length - 1 ? 0 : prev + 1));
    }, [images.length]);

    // Get URL from image (handles both string and object)
    const getImageUrl = (image) => {
        if (typeof image === "string") return image;
        return image?.[urlKey] || image?.url || image?.preview || "";
    };

    if (!isOpen || images.length === 0) return null;

    const currentImage = images[currentIndex];
    const imageUrl = getImageUrl(currentImage);

    return (
        <div
            className="fixed inset-0 z-50 flex items-center justify-center bg-black/90"
            onClick={onClose}
        >
            {/* Close Button */}
            <Button
                type="button"
                isIconOnly
                size="sm"
                variant="flat"
                className="absolute top-4 right-4 z-10 bg-white/10 text-white hover:bg-white/20"
                onPress={onClose}
            >
                <LuX size={18} />
            </Button>

            {/* Image Counter */}
            {images.length > 1 && (
                <div className="absolute top-4 left-4 z-10 rounded-full bg-white/10 px-3 py-1 text-sm text-white">
                    {currentIndex + 1} / {images.length}
                </div>
            )}

            {/* Previous Button */}
            {images.length > 1 && (
                <Button
                    type="button"
                    isIconOnly
                    size="lg"
                    variant="flat"
                    className="absolute left-4 z-10 bg-white/10 text-white hover:bg-white/20"
                    onPress={(e) => {
                        e.stopPropagation?.();
                        goToPrev();
                    }}
                >
                    <LuChevronLeft size={28} />
                </Button>
            )}

            {/* Main Image */}
            <img
                src={imageUrl}
                alt={`Image ${currentIndex + 1}`}
                className="max-h-[90vh] max-w-[90vw] rounded-lg object-contain"
                onClick={(e) => e.stopPropagation()}
            />

            {/* Next Button */}
            {images.length > 1 && (
                <Button
                    type="button"
                    isIconOnly
                    size="lg"
                    variant="flat"
                    className="absolute right-4 z-10 bg-white/10 text-white hover:bg-white/20"
                    onPress={(e) => {
                        e.stopPropagation?.();
                        goToNext();
                    }}
                >
                    <LuChevronRight size={28} />
                </Button>
            )}

            {/* Thumbnail Strip (optional, for multiple images) */}
            {images.length > 1 && (
                <div className="absolute bottom-4 left-1/2 z-10 flex -translate-x-1/2 gap-2">
                    {images.map((img, index) => (
                        <button
                            key={index}
                            onClick={(e) => {
                                e.stopPropagation();
                                setCurrentIndex(index);
                            }}
                            className={`h-12 w-12 overflow-hidden rounded-lg border-2 transition-all ${
                                index === currentIndex
                                    ? "border-white"
                                    : "border-transparent opacity-50 hover:opacity-100"
                            }`}
                        >
                            <img
                                src={getImageUrl(img)}
                                alt={`Thumbnail ${index + 1}`}
                                className="h-full w-full object-cover"
                            />
                        </button>
                    ))}
                </div>
            )}
        </div>
    );
};

export default ImageLightbox;
